import React from "react";

import { cn } from "../../utils/cn";

// Typography variants
export type TypographyVariant =
	| "h1" | "h2" | "h3" | "h4" | "h5"
	| "txt-40" | "txt-30" | "txt-25" | "txt-18" | "txt-16" | "txt-16-medium" | "txt-16-light" | "txt-14";

// Color variants
export type TypographyColor =
	| "blu-scuro" | "blu-medio" | "blu-chiaro" | "azzurro"
	| "verde" | "giallo" | "rosso" | "bianco" | "grigio" | "transparent";

// Component mapping for different variants
const variantToComponent: Record<TypographyVariant, React.ElementType> = {
	"h1": "h1",
	"h2": "h2",
	"h3": "h3",
	"h4": "h4",
	"h5": "h5",
	"txt-40": "p",
	"txt-30": "p",
	"txt-25": "p",
	"txt-18": "p",
	"txt-16": "span",
	"txt-16-medium": "span",
	"txt-16-light": "span",
	"txt-14": "span",
};

// Color mapping - now using direct color names
const colorClasses: Record<TypographyColor, string> = {
	"blu-scuro": "text-blu-scuro",
	"blu-medio": "text-blu-medio",
	"blu-chiaro": "text-blu-chiaro",
	"azzurro": "text-azzurro",
	"verde": "text-verde",
	"giallo": "text-giallo",
	"rosso": "text-rosso",
	"bianco": "text-bianco",
	"grigio": "text-grigio",
	"transparent": "text-transparent",
};

// Variant mapping to Tailwind classes using new text-* convention and font-* utilities
const variantClasses: Record<TypographyVariant, string> = {
	"h1": "text-h1 font-rubik",
	"h2": "text-h2 font-rubik",
	"h3": "text-h3 font-rubik",
	"h4": "text-h4 font-rubik",
	"h5": "text-h5 font-rubik",
	"txt-40": "text-txt-40 font-open-sans",
	"txt-30": "text-txt-30 font-open-sans",
	"txt-25": "text-txt-25 font-open-sans",
	"txt-18": "text-txt-18 font-open-sans",
	"txt-16": "text-txt-16 font-open-sans",
	"txt-16-medium": "text-txt-16-medium font-open-sans",
	"txt-16-light": "text-txt-16-light font-open-sans",
	"txt-14": "text-txt-14 font-open-sans",
};

export interface TypographyProps {
	variant: TypographyVariant;
	color?: TypographyColor;
	children: React.ReactNode;
	className?: string;
	as?: React.ElementType;
	onClick?: () => void;
	setDangerouslySetInnerHTML?: boolean;
}

export const Typography: React.FC<TypographyProps> = ({
	variant,
	color = "blu-scuro",
	children,
	className,
	as,
	onClick,
	setDangerouslySetInnerHTML,
}) => {
	const Component = as || variantToComponent[variant];
	const variantClass = variantClasses[variant];
	const colorClass = colorClasses[color];

	if (setDangerouslySetInnerHTML) {
		return (
			<Component
				className={cn(
					variantClass,
					colorClass,
					onClick && "cursor-pointer select-none",
					className
				)}
				dangerouslySetInnerHTML={{ __html: children as string }}
				onClick={onClick}
			/>
		);
	}

	return (
		<Component
			className={cn(
				variantClass,
				colorClass,
				onClick && "cursor-pointer select-none",
				className
			)}
			onClick={onClick}
		>{children}</Component>
	);
};

// Convenience components for each variant
export const H1: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="h1" {...props} />
);

export const H2: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="h2" {...props} />
);

export const H3: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="h3" {...props} />
);

export const H4: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="h4" {...props} />
);

export const H5: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="h5" {...props} />
);

export const Text40: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="txt-40" {...props} />
);

export const Text30: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="txt-30" {...props} />
);

export const Text25: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="txt-25" {...props} />
);

export const Text18: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="txt-18" {...props} />
);

export const Text16: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="txt-16" {...props} />
);

export const Text16Medium: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="txt-16-medium" {...props} />
);

export const Text16Light: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="txt-16-light" {...props} />
);

export const Text14: React.FC<Omit<TypographyProps, "variant"> & { variant?: never }> = (props) => (
	<Typography variant="txt-14" {...props} />
);
